import { Component, input, ChangeDetectionStrategy } from '@angular/core';
import { ShIconComponent } from '@gbr-monorepo/gbr-shui';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-skip-link',
    templateUrl: './skip-link.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TranslateModule, ShIconComponent]
})
export class SkipLinkComponent {
    public readonly targetId = input<string>('');
    public readonly label = input<string>('');
    public readonly disabled = input(false);

    protected onSkipLinkClick(event: Event): void {
        event.preventDefault();

        const container = document.getElementById(this.targetId());
        if (!container) {
            (event.target as HTMLElement).blur();
            return;
        }

        const focusTarget = this.getFocusTarget(container);

        requestAnimationFrame(() => {
            this.scrollToTarget(focusTarget);
            focusTarget.focus({ preventScroll: true });
            this.applyVisibleFocus(focusTarget);
        });
    }

    private getFocusTarget(container: HTMLElement): HTMLElement {
        if (container.hasAttribute('tabindex')) {
            return container;
        }

        const focusableSelector =
            'button:not([disabled]), sh-button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

        if (container.matches(focusableSelector)) {
            return container;
        }

        const focusTarget = container.querySelector<HTMLElement>(focusableSelector);
        if (focusTarget) {
            return focusTarget;
        }

        container.setAttribute('tabindex', '-1');
        return container;
    }

    private applyVisibleFocus(target: HTMLElement): void {
        this.clearPreviousVisibleFocus();

        target.dataset['skipLinkOutline'] = target.style.outline;
        target.dataset['skipLinkOutlineOffset'] = target.style.outlineOffset;
        target.classList.add('item-focused');
        target.style.outline = '2px solid rgb(var(--ui-2))';
        target.style.outlineOffset = '2px';
        target.addEventListener('blur', () => this.clearVisibleFocus(target), { once: true });
    }

    private clearPreviousVisibleFocus(): void {
        document
            .querySelectorAll<HTMLElement>('[data-skip-link-outline], [data-skip-link-outline-offset]')
            .forEach(element => this.clearVisibleFocus(element));
    }

    private clearVisibleFocus(target: HTMLElement): void {
        target.classList.remove('item-focused');
        target.style.outline = target.dataset['skipLinkOutline'] ?? '';
        target.style.outlineOffset = target.dataset['skipLinkOutlineOffset'] ?? '';
        delete target.dataset['skipLinkOutline'];
        delete target.dataset['skipLinkOutlineOffset'];
    }

    private scrollToTarget(target: HTMLElement): void {
        const headerOffset = this.getHeaderOffset();
        const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'auto'
        });
    }

    private getHeaderOffset(): number {
        const headerHeight = document.querySelector('app-shared-header')?.getBoundingClientRect().height;
        if (headerHeight) {
            return headerHeight;
        }

        const appHeaderHeight = getComputedStyle(document.documentElement).getPropertyValue('--app-header-height');
        const parsedHeight = Number.parseFloat(appHeaderHeight);

        return Number.isNaN(parsedHeight) ? 0 : parsedHeight;
    }
}
